import React, { useCallback, useEffect, useState } from 'react';
import SwipeableViews from 'react-swipeable-views';
import Div100vh from 'react-div-100vh';
import classNames from 'classnames';
import Modal from '../common/Modal';
import { CmsAsset } from '../../integrations/contentful/types';
import { t } from '../../utils/i18n';
import { baseXUnit, breakpoints, color, spacing } from '../../styles/theme';
import Icon, { IconName } from '../common/Icon';
import DivButton from '../a11y/DivButton';
import DisplayOn from '../common/DisplayOn';
import Paragraph from '../elements/Paragraph';
import MediaGallerySlide, {
  mediaGalleryModalCaptionDesktopHeightBase,
  mediaGalleryModalSlideLeftPaddingBase
} from './MediaGallerySlide';
import MediaGalleryModalHeader, {
  mediaGalleryModalHeaderDesktopHeightBase
} from './MediaGalleryModalHeader';
import MediaGallerySidebar from './MediaGallerySidebar';

const mediaGalleryModalNavigationButtonFromSideBase = 4;
const MediaGalleryModalNavigationButton: React.FunctionComponent<{
  onClick: () => void;
  position: 'left' | 'right';
  disabled?: boolean;
  hideButtonsMediumAndLarge: boolean; // controls only medium + large.
}> = ({ onClick, disabled, position, hideButtonsMediumAndLarge }) => {
  const iconName = ('nav-arrow-' + position) as IconName;
  return (
    <DivButton
      className={classNames('media-gallery-modal-navigation-button', position, {
        disabled
      })}
      disabled={disabled}
      onClick={onClick}
    >
      <div className="icon-wrapper">
        <Icon name={iconName} size={4} color="white" />
      </div>
      {/*language=CSS*/}
      <style jsx global>{`
        .media-gallery-modal-navigation-button {
          position: absolute;
          width: auto;
          max-width: 45%;
          top: 0;
          height: 100%;
          background: transparent;
          align-items: center;
          opacity: 0;
          transition: opacity 0.2s ease;
          display: flex;
          padding-bottom: ${baseXUnit(
            mediaGalleryModalCaptionDesktopHeightBase
          )};
        }
        .media-gallery-modal-navigation-button:hover {
          opacity: 1;
        }
        .media-gallery-modal-navigation-button.left {
          left: 0;
          margin-left: ${baseXUnit(
            mediaGalleryModalSlideLeftPaddingBase +
              mediaGalleryModalNavigationButtonFromSideBase
          )};
        }
        .media-gallery-modal-navigation-button.right {
          right: 0;
          margin-right: ${baseXUnit(
            mediaGalleryModalNavigationButtonFromSideBase
          )};
          justify-content: flex-end;
        }
        .media-gallery-modal-navigation-button.disabled > .icon-wrapper {
          display: none;
        }
        @media ${breakpoints.medium} {
          .media-gallery-modal-navigation-button {
            /* Show on swipeable (medium + large) devices, so that user don't try swipe from button. */
            opacity: ${hideButtonsMediumAndLarge ? 1 : 0};
          }
        }
        @media ${breakpoints.extraLarge} {
          .media-gallery-modal-navigation-button {
            width: 300px;
            opacity: 0;
          }
          .media-gallery-modal-navigation-button.right {
            margin-right: initial;
            padding-right: ${baseXUnit(
              mediaGalleryModalNavigationButtonFromSideBase
            )};
          }
          .media-gallery-modal-navigation-button.right {
            margin-left: initial;
            padding-left: ${baseXUnit(
              mediaGalleryModalSlideLeftPaddingBase +
                mediaGalleryModalNavigationButtonFromSideBase
            )};
          }
        }
      `}</style>
      {/*language=CSS*/}
      <style jsx>{`
        .icon-wrapper {
          border-radius: ${spacing.one};
          padding: ${spacing.one};
          background: rgba(0, 0, 0, 0.2);
          display: inline-block;
          position: absolute;
        }
      `}</style>
    </DivButton>
  );
};

export interface MediaGalleryItemPhase {
  iconName: IconName;
  title: string;
  scrollTarget: string;
}
export interface MediaGalleryItem {
  phase?: MediaGalleryItemPhase;
  media: CmsAsset;
}

export interface MediaGalleryModalProps {
  initialSlideIndex?: number;
  items: MediaGalleryItem[];
  closeDialog: () => void;
  showDialog: boolean;
}

const MediaGalleryModal: React.FunctionComponent<MediaGalleryModalProps> = ({
  initialSlideIndex,
  items,
  closeDialog,
  showDialog
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialSlideIndex || 0);
  const [isZoomed, setIsZoomed] = useState(false);
  const [showOverlays, setShowOverlays] = useState(false);
  useEffect(() => {
    if (showDialog && initialSlideIndex !== undefined) {
      setCurrentIndex(initialSlideIndex);
    }
  }, [showDialog, initialSlideIndex]);
  useEffect(() => {
    setIsZoomed(false);
    setShowOverlays(true);
  }, [currentIndex]);
  const currentItem = items[currentIndex];
  const currentPhase = currentItem && currentItem.phase;
  const handleZoomChange = useCallback(
    (zoomInfo: { scale: number }) => {
      // Allow 5% leeway
      const newIsZoomed = zoomInfo.scale > 1.05;
      if (newIsZoomed !== isZoomed) {
        setIsZoomed(newIsZoomed);
      }
      if (newIsZoomed) {
        setShowOverlays(false);
      }
    },
    [isZoomed]
  );
  const toggleShowOverlays = useCallback(() => {
    setShowOverlays(!showOverlays);
  }, [showOverlays]);
  const overlayClasses = {
    overlay: true,
    'overlay-hidden': !showOverlays
  };
  const previousIndex = currentIndex - 1;
  const nextIndex = currentIndex + 1;
  return (
    <>
      <Modal
        className="media-gallery-modal"
        data-testid="media-gallery-modal"
        data-cy="media-gallery-modal"
        isOpen={showDialog}
        onDismiss={closeDialog}
        ariaLabel={t('Mediagalleria')}
        variant="fullscreen"
      >
        {showDialog && (
          <Div100vh>
            <div className="content">
              <div className={classNames('content-header', overlayClasses)}>
                <MediaGalleryModalHeader
                  closeDialog={closeDialog}
                  currentSlideNumber={currentIndex + 1}
                  totalSlideNumber={items.length}
                  phase={currentPhase}
                />
              </div>

              <div className="media-layout">
                <div className="media-area">
                  <DivButton
                    className="show-overlays-toggle"
                    onClick={toggleShowOverlays}
                  >
                    <div className="swipeable-views-wrapper">
                      <SwipeableViews
                        index={currentIndex}
                        onChangeIndex={(index) => setCurrentIndex(index)}
                        containerStyle={{ height: '100%' }}
                        resistance
                      >
                        {items.map((item, slideIndex) => {
                          const isCurrentSlide = slideIndex === currentIndex;
                          return (
                            <MediaGallerySlide
                              key={item.media.sys.id + slideIndex}
                              isZoomed={isZoomed}
                              isCurrentSlide={isCurrentSlide}
                              onZoomChange={handleZoomChange}
                              media={item.media}
                            />
                          );
                        })}
                      </SwipeableViews>
                    </div>
                  </DivButton>
                  <DisplayOn medium large>
                    <MediaGalleryModalNavigationButton
                      position="left"
                      disabled={previousIndex < 0}
                      onClick={() => setCurrentIndex(previousIndex)}
                      hideButtonsMediumAndLarge={showOverlays}
                    />
                    <MediaGalleryModalNavigationButton
                      position="right"
                      disabled={nextIndex >= items.length}
                      onClick={() => setCurrentIndex(nextIndex)}
                      hideButtonsMediumAndLarge={showOverlays}
                    />
                  </DisplayOn>
                  <div className={classNames('caption', overlayClasses)}>
                    <Paragraph>
                      {currentItem && currentItem.media.fields.description}
                    </Paragraph>
                  </div>
                </div>
                {items.length > 1 && (
                  <div className="media-sidebar" data-testid="media-sidebar">
                    <DisplayOn medium large>
                      <MediaGallerySidebar
                        items={items}
                        currentIndex={currentIndex}
                        onItemClick={(index) => {
                          setCurrentIndex(index);
                        }}
                      />
                    </DisplayOn>
                  </div>
                )}
              </div>
              {/*language=CSS*/}
              <style jsx>{`
                .content {
                  background-color: #000;
                  height: 100%;
                  display: flex;
                  flex-direction: column;
                  position: relative;
                }

                .content-header {
                  position: absolute;
                  z-index: 100;
                  top: 0;
                  left: 0;
                  width: 100%;
                }

                .caption {
                  position: absolute;
                  z-index: 100;
                  bottom: 0;
                  left: 0;
                  width: 100%;
                  padding: ${baseXUnit(2)};
                  background-image: linear-gradient(
                    to bottom,
                    rgba(0, 0, 0, 0),
                    rgba(0, 0, 0, 0.9)
                  );
                  color: ${color.white};
                  /* Pass clicks to the underlying content */
                  pointer-events: none;
                }

                .overlay {
                  opacity: 1;
                  transition: opacity 0.35s ease;
                }

                .overlay.overlay-hidden {
                  opacity: 0;
                  pointer-events: none;
                }

                .content :global(.show-overlays-toggle) {
                  height: 100%;
                }

                .media-layout {
                  display: flex;
                  height: 100%;
                  position: relative;
                }

                .media-area {
                  position: relative;
                  height: 100%;
                  flex: 1;
                  overflow: hidden;
                }

                .swipeable-views-wrapper {
                  height: 100%;
                }

                /* SwipeableViews has a container with no class, so we need to select it like this. */
                .swipeable-views-wrapper > :global(div) {
                  height: 100%;
                }

                @media ${breakpoints.medium} {
                  .content-header {
                    position: relative;
                  }

                  .media-layout {
                    display: flex;
                    height: calc(
                      100% -
                        ${baseXUnit(mediaGalleryModalHeaderDesktopHeightBase)}
                    );
                  }

                  .caption {
                    height: ${baseXUnit(
                      mediaGalleryModalCaptionDesktopHeightBase
                    )};
                    padding: ${baseXUnit(5)};
                    padding-left: ${baseXUnit(3)};
                  }

                  .media-sidebar {
                    width: ${baseXUnit(23)};
                    flex-shrink: 0;
                    overflow-y: auto;
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                  }

                  .media-sidebar::-webkit-scrollbar {
                    display: none;
                  }
                }

                @media ${breakpoints.extraLarge} {
                  .caption.overlay.overlay-hidden,
                  .content-header.overlay.overlay-hidden {
                    /* Disable elements hiding outside extra large */
                    opacity: 1;
                    /* Allow clicking elements when user has used scrolling and our zooming triggered. 
                    We should maybe prevent zooming in extra large somehow, because it's not really useful in extra large. */
                    pointer-events: auto;
                  }
                }
              `}</style>
            </div>
          </Div100vh>
        )}
      </Modal>
    </>
  );
};

export default MediaGalleryModal;
